import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'src/state/redux';
import { promptValuesActions } from 'src/state/redux/slice/promptValues';
import {
  selectActivePromptValuesByType,
  selectPromptValuesLoading,
} from 'src/state/redux/selector/promptValues';
import { PromptValues, PromptType } from '@alucio/aws-beacon-amplify/src/models';
import { GenericToast, ToastOrientations, useToast } from '@alucio/lux-ui';
import * as logger from 'src/utils/logger';

export interface PromptTypeConfig {
  maxLimit: number;
  characterLimit?: number | null;
  displayName: string;
  singularName: string;
  pluralName: string;
  messages: {
    createSuccess: string;
    updateSuccess: string;
    deleteSuccess: string;
    maxLimitReached: string;
  };
  modalTitles: {
    create: string;
    edit: string;
  };
  emptyState: {
    label: string;
    actionLabel: string;
  };
  placeholder: string;
}

export interface PromptValuesStateType {
  promptType: PromptType;
  config: PromptTypeConfig;
  values: PromptValues[];
  loading: boolean;
  canAddMore: boolean;
  selectedValues: Set<string>;
  handleCreate: (text: string) => void;
  handleUpdate: (value: PromptValues, updatedText: string) => void;
  handleDelete: (value: PromptValues) => void;
  handleToggleSelection: (valueId: string) => void;
  isSelected: (valueId: string) => boolean;
}

export const PromptValuesStateContext = createContext<PromptValuesStateType | null>(null!);

export const usePromptValuesState = (): PromptValuesStateType => {
  const context = useContext(PromptValuesStateContext);
  if (!context) {
    throw new Error('usePromptValuesState must be used within a PromptValuesStateProvider');
  }
  return context;
};

interface PromptValuesStateProviderProps {
  promptType: PromptType;
  children: React.ReactNode;
}

export const AI_SUMMARY_PROMPT_TYPES = new Set<PromptType>([
  PromptType.AI_SUMMARY_DOC_TYPE,
  PromptType.AI_SUMMARY_INSTRUCTIONS,
]);

export const isAISummaryPromptType = (type: PromptType): boolean => AI_SUMMARY_PROMPT_TYPES.has(type);

export const PROMPT_TYPE_CONFIG: Record<PromptType, PromptTypeConfig> = {
  [PromptType.DECK_COMPLIANCE_RULE]: {
    maxLimit: 10,
    displayName: 'Compliance Rules',
    singularName: 'Rule',
    pluralName: 'Rules',
    messages: {
      createSuccess: 'Compliance rule created successfully',
      updateSuccess: 'Compliance rule updated successfully',
      deleteSuccess: 'Compliance rule deleted successfully',
      maxLimitReached: 'You have reached the maximum limit of 10 compliance rules',
    },
    modalTitles: {
      create: 'Create rule',
      edit: 'Edit rule',
    },
    emptyState: {
      label: 'Add a rule to get started.',
      actionLabel: 'Add rule',
    },
    placeholder: 'Write your rule here',
  },
  [PromptType.TENANT_BACKGROUND]: {
    maxLimit: 1,
    displayName: 'Tenant Background',
    singularName: 'Tenant Background',
    pluralName: 'Tenant Backgrounds',
    messages: {
      createSuccess: 'Tenant background created successfully',
      updateSuccess: 'Tenant background updated successfully',
      deleteSuccess: 'Tenant background deleted successfully',
      maxLimitReached: 'You can only have one tenant background',
    },
    modalTitles: {
      create: 'Create background',
      edit: 'Edit background',
    },
    emptyState: {
      label: 'Add tenant background to get started.',
      actionLabel: 'Add background',
    },
    placeholder: 'Write your tenant background here',
  },
  [PromptType.AI_SUMMARY_DOC_TYPE]: {
    maxLimit: 3,
    displayName: 'AI Summary Document Types',
    singularName: 'Document Type',
    pluralName: 'Document Types',
    messages: {
      createSuccess: 'AI summary document type created successfully',
      updateSuccess: 'AI summary document type updated successfully',
      deleteSuccess: 'AI summary document type deleted successfully',
      maxLimitReached: 'You can only prioritize up to 3 document types',
    },
    modalTitles: {
      create: 'Add document type',
      edit: 'Edit document type',
    },
    emptyState: {
      label: 'Add document types to prioritize AI summaries.',
      actionLabel: 'Add document type',
    },
    placeholder: 'Example: Poster/Presentation',
  },
  [PromptType.AI_SUMMARY_INSTRUCTIONS]: {
    maxLimit: 1,
    displayName: 'AI Summary Instructions',
    singularName: 'Instruction',
    pluralName: 'Instructions',
    messages: {
      createSuccess: 'AI summary instructions created successfully',
      updateSuccess: 'AI summary instructions updated successfully',
      deleteSuccess: 'AI summary instructions deleted successfully',
      maxLimitReached: 'You can only have one AI summary instruction entry',
    },
    modalTitles: {
      create: 'Add instructions',
      edit: 'Edit instructions',
    },
    emptyState: {
      label: 'Add instructions to guide AI summaries.',
      actionLabel: 'Add instructions',
    },
    placeholder: 'Write AI summary instructions here',
  },
  [PromptType.AI_SEARCH_ABBREVIATIONS]: {
    maxLimit: 1,
    characterLimit: null,
    displayName: 'AI Search Abbreviations',
    singularName: 'Abbreviation Catalog',
    pluralName: 'Abbreviation Catalogs',
    messages: {
      createSuccess: 'AI search abbreviations created successfully',
      updateSuccess: 'AI search abbreviations updated successfully',
      deleteSuccess: 'AI search abbreviations deleted successfully',
      maxLimitReached: 'You can only have one AI search abbreviation catalog',
    },
    modalTitles: {
      create: 'Add abbreviation catalog',
      edit: 'Edit abbreviation catalog',
    },
    emptyState: {
      label: 'Add abbreviations to improve AI search accuracy.',
      actionLabel: 'Add abbreviations',
    },
    placeholder: 'Example: DB11 = DESTINY-Breast 11',
  },
};

const PromptValuesStateProvider: React.FC<PromptValuesStateProviderProps> = ({
  promptType,
  children,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const values = useSelector((state) => selectActivePromptValuesByType(state, promptType));
  const loading = useSelector(selectPromptValuesLoading);
  const config = useMemo(() => PROMPT_TYPE_CONFIG[promptType], [promptType]);

  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set());

  const currentToastIdRef = useRef<string | null>(null);
  const lastToastTimeRef = useRef<number>(0);
  const lastToastMessageRef = useRef<string>('');

  useEffect(() => {
    setSelectedValues((prevSelected) => {
      const newSelected = new Set(prevSelected);
      const currentIds = new Set(values.map((v) => v.id));

      // If this is the first load (no previous selections), select all values by default
      if (prevSelected.size === 0 && values.length > 0) {
        values.forEach((value) => {
          newSelected.add(value.id);
        });
      } else {
        // Only clean up selections for values that no longer exist
        Array.from(newSelected).forEach((id) => {
          if (!currentIds.has(id)) {
            newSelected.delete(id);
          }
        });
      }

      return newSelected;
    });
  }, [values]);

  const canAddMore = useMemo(() => values.length < config.maxLimit, [values.length, config.maxLimit]);

  const showToast = useCallback((component: JSX.Element) => {
    const message = component.props?.description || '';
    const now = Date.now();

    if (message === lastToastMessageRef.current && now - lastToastTimeRef.current < 500) {
      return;
    }

    if (currentToastIdRef.current && toast?.remove) {
      toast.remove(currentToastIdRef.current);
    }

    if (toast?.add) {
      const toastId = toast.add(component, ToastOrientations.TOP_RIGHT);
      currentToastIdRef.current = toastId;
      lastToastTimeRef.current = now;
      lastToastMessageRef.current = message;
    }
  }, [toast]);

  const handleCreate = useCallback((text: string) => {
    if (!text.trim()) {
      showToast(
        <GenericToast
          title="Error"
          description={`${config.singularName} text cannot be empty`}
          status="error"
        />,
      );
      return;
    }

    if (!canAddMore) {
      showToast(
        <GenericToast
          title="Limit Reached"
          description={config.messages.maxLimitReached}
          status="warning"
        />,
      );
      return;
    }

    dispatch(promptValuesActions.create({
      promptType,
      text: text.trim(),
      callback: (value: PromptValues) => {
        showToast(
          <GenericToast
            title="Success"
            description={config.messages.createSuccess}
            status="success"
          />,
        );
        logger.compliance?.info(`${config.singularName} created successfully`, { valueId: value.id });
      },
    }));
  }, [dispatch, showToast, promptType, config, canAddMore]);

  const handleUpdate = useCallback((value: PromptValues, updatedText: string) => {
    if (!updatedText.trim()) {
      showToast(
        <GenericToast
          title="Error"
          description={`${config.singularName} text cannot be empty`}
          status="error"
        />,
      );
      return;
    }

    dispatch(promptValuesActions.updatePromptValue({
      value,
      updatedText: updatedText.trim(),
      callback: (updatedValue: PromptValues) => {
        showToast(
          <GenericToast
            title="Success"
            description={config.messages.updateSuccess}
            status="success"
          />,
        );
        logger.compliance?.info(`${config.singularName} updated successfully`, { valueId: updatedValue.id });
      },
    }));
  }, [dispatch, showToast, config]);

  const handleDelete = useCallback((value: PromptValues) => {
    showToast(
      <GenericToast
        title="Success"
        description={config.messages.deleteSuccess}
        status="success"
      />,
    );
    logger.compliance?.info(`${config.singularName} deleted successfully`, { valueId: value.id });
    dispatch(promptValuesActions.deleteValue(value));
  }, [dispatch, showToast, config]);

  const handleToggleSelection = useCallback((valueId: string) => {
    setSelectedValues((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(valueId)) {
        newSet.delete(valueId);
      } else {
        newSet.add(valueId);
      }
      return newSet;
    });
  }, []);

  const isSelected = useCallback((valueId: string) => {
    return selectedValues.has(valueId);
  }, [selectedValues]);

  const contextValue: PromptValuesStateType = useMemo(() => ({
    promptType,
    config,
    values,
    loading,
    canAddMore,
    selectedValues,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleToggleSelection,
    isSelected,
  }), [
    promptType,
    config,
    values,
    loading,
    canAddMore,
    selectedValues,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleToggleSelection,
    isSelected,
  ]);

  return (
    <PromptValuesStateContext.Provider value={contextValue}>
      {children}
    </PromptValuesStateContext.Provider>
  );
};

export default PromptValuesStateProvider;
